"use client";

import classNames from "classnames";
import styles from "./Drawer.module.scss";
import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { DrawerTypes } from "./Drawer.types";

const easeOut = [0.4, 0, 0.2, 1] as const;

const Drawer = ({
  isOpen,
  onClose,
  children,
  ariaLabel,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  stacked = false,
}: DrawerTypes) => {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  const variants = useMemo(() => {
    const instant = { duration: 0.01 };
    if (reducedMotion) {
      return {
        root: {
          hidden: { transition: instant },
          visible: { transition: instant },
        },
        backdrop: {
          hidden: { opacity: 0, transition: instant },
          visible: { opacity: 1, transition: instant },
        },
        panel: {
          hidden: { x: 0, opacity: 1, transition: instant },
          visible: { x: 0, opacity: 1, transition: instant },
        },
      };
    }
    return {
      root: {
        hidden: {},
        visible: {},
      },
      backdrop: {
        hidden: {
          opacity: 0,
          transition: { duration: 0.18, ease: easeOut },
        },
        visible: {
          opacity: 1,
          transition: { duration: 0.22, ease: easeOut },
        },
      },
      panel: {
        hidden: {
          x: "100%",
          transition: { type: "spring" as const, damping: 34, stiffness: 400, mass: 0.82 },
        },
        visible: {
          x: 0,
          transition: { type: "spring" as const, damping: 32, stiffness: 380, mass: 0.85 },
        },
      },
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const target = event.target as HTMLElement;
      // Portaled Select menus / calendar popovers mount on document.body; treat as inside.
      if (target.closest("[data-select-dropdown='true']")) return;
      if (target.closest("[data-calendar-popover='true']")) return;
      if (ref.current && !ref.current.contains(target)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (!closeOnEscape) return;
      if (e.key !== "Escape") return;
      // Let an open Select/calendar popover consume the first Escape; close drawer next.
      if (document.querySelector("[data-select-dropdown='true']")) return;
      if (document.querySelector("[data-calendar-popover='true']")) return;
      onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, closeOnOutsideClick, closeOnEscape]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <AnimatePresence
      onExitComplete={() => {
        document.body.style.overflow = "";
      }}
    >
      {isOpen ? (
        <motion.div
          key="drawer-root"
          className={classNames(styles["comp"], stacked && styles["comp--stacked"])}
          variants={variants.root}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div
            className={styles["comp__background"]}
            variants={variants.backdrop}
            onClick={closeOnOutsideClick ? onClose : undefined}
            aria-hidden
          />
          <div className={styles["comp__container"]}>
            <motion.div
              ref={ref}
              className={styles["comp__panel"]}
              variants={variants.panel}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
            >
              {children}
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export { Drawer };
